/**
 * The shared steps between "a draft exists" and "the doctor is looking at the
 * saved note". Three call sites run them: the end-of-call flow (`ZoomPanel`),
 * and — both in `EncounterWorkspace`, which provides the form bridge — the
 * interrupted-visit finish and draft recovery on re-entry.
 *
 * They are separate functions rather than one helper because the order differs:
 * ZoomPanel plays a completion animation between the disclaimer and the reveal,
 * and that choreography stays with the panel. What must NOT diverge is the
 * disclaimer itself — every path that opens an AI note has to show it.
 */
import { logError } from '@/utils/errorUtils'
import { confirmAiNote } from './confirmAiNote'
import type { FinalizeScribeResult } from './useFinalizeScribe'
import type {
  ScribeFormBridgeContextValue,
  StructuredScribeDraft,
} from '../context/ScribeFormBridgeContext'

export interface PreparedDraft {
  draft: StructuredScribeDraft
  /** False when the AI produced nothing — the note is saved from the form's current state instead. */
  hasAiSignal: boolean
}

/** Narrow a finalize/generate result to the fields the Progress Note form consumes. */
export function toStructuredDraft(result: FinalizeScribeResult): PreparedDraft {
  const draft: StructuredScribeDraft = {
    summary: result.summary,
    conditions: result.conditions,
    procedures: result.procedures,
    medications: result.medications,
    observations: result.observations,
    allergies: result.allergies,
    serviceRequests: result.serviceRequests,
    billing: result.billing,
  }
  const hasAiSignal = Boolean(
    result.summary ||
    result.conditions.length ||
    result.medications.length ||
    result.observations.length ||
    result.allergies.length ||
    result.procedures.length ||
    result.serviceRequests.length ||
    result.billing,
  )
  return { draft, hasAiSignal }
}

/**
 * PD-1181 — merge into the (still hidden) form and flush its autosave before any
 * modal, so closing the tab at the disclaimer can't lose the draft. Idempotent
 * `/soap-note`; no reveal, no navigation.
 */
export async function persistDraftBeforePrompt(
  bridge: ScribeFormBridgeContextValue | null,
  { draft, hasAiSignal }: PreparedDraft,
  logScope: string,
): Promise<void> {
  if (!bridge || !hasAiSignal) return
  try {
    await bridge.populateFromAi(draft, { autoSubmit: false })
    await bridge.flushDraft()
  } catch (err) {
    logError(logScope, 'Pre-disclaimer draft persist failed', err)
  }
}

/**
 * The single acknowledgement gate before an AI note opens (PD-1110): the
 * disclaimer, carrying any HIGH-severity quality flags. Those come from the
 * judge or the code checks, and the code checks still run when the judge is
 * down ('unavailable'), so every outcome but 'pass' carries them (PD-2016).
 * No-op when the AI produced nothing — there is nothing to disclaim.
 */
export async function confirmDraftDisclaimer(
  result: FinalizeScribeResult,
  hasAiSignal: boolean,
): Promise<void> {
  if (!hasAiSignal) return
  const verification = result.verification
  await confirmAiNote(verification && verification.outcome !== 'pass' ? verification.issues : [])
}

/**
 * Swap the chrome to the Progress Note pane and commit: apply the AI draft and
 * let the form auto-submit, or — when the AI produced nothing — save whatever
 * the form already holds so the doctor still lands on a preliminary note.
 */
export async function revealDraft(
  bridge: ScribeFormBridgeContextValue | null,
  { draft, hasAiSignal }: PreparedDraft,
): Promise<void> {
  if (!bridge) return
  bridge.showSoapNote()
  if (hasAiSignal) {
    await bridge.populateFromAi(draft, { autoSubmit: true })
  } else {
    await bridge.submitCurrentDraft()
  }
}

/**
 * The whole sequence, for callers with nothing to interleave. `ZoomPanel` calls
 * the steps individually instead, because its completion animation sits between
 * the disclaimer and the reveal.
 */
export async function applyDraftAndReveal(
  bridge: ScribeFormBridgeContextValue | null,
  result: FinalizeScribeResult,
  logScope: string,
): Promise<void> {
  const prepared = toStructuredDraft(result)
  await persistDraftBeforePrompt(bridge, prepared, logScope)
  await confirmDraftDisclaimer(result, prepared.hasAiSignal)
  await revealDraft(bridge, prepared)
}
